// [Codex 2026-07-09] Voice CAO dock pure helpers and DOM event readers.
import type {
  Dispatch,
  PointerEvent as ReactPointerEvent,
  SetStateAction,
} from "react";
import {
  responseScenarios,
  type ResponseScenario,
} from "../../voice-cao-dock-data";
import { buildPendingVoiceResponse } from "../../voice-cao-pending-response";
import {
  resolveVoiceOrbState,
  type VoiceOrbState,
  type VoiceRuntimeState,
} from "../../types/voice";

const DOCK_POSITION_KEY = "fintheon:voice-cao-dock-position:v1";

export interface VoiceCaoContext {
  kind: "price" | "chart" | "source" | "task";
  label: string;
  detail: string;
}

export interface DockPosition {
  x: number;
  y: number;
}

export interface DockVisualBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const DEFAULT_DOCK_VISUAL_BOUNDS: DockVisualBounds = {
  left: 0,
  top: -32,
  right: 340,
  bottom: 236,
};

export function stateLabel(runtimeState: VoiceRuntimeState): string {
  if (runtimeState === "listening") return "Listening";
  if (runtimeState === "speaking") return "Speaking";
  if (runtimeState === "thinking") return "Thinking";
  if (runtimeState === "error") return "Error";
  return "Ready";
}

export function voiceCaoOrbState({
  runtimeState,
  shouldShowBottomDrawer,
}: {
  runtimeState: VoiceRuntimeState;
  shouldShowBottomDrawer: boolean;
}): VoiceOrbState {
  if (runtimeState === "error") return "error";
  if (shouldShowBottomDrawer) return "complete";
  if (runtimeState === "thinking") return "thinking";
  if (runtimeState === "idle") return "listening";
  return resolveVoiceOrbState(runtimeState, false);
}

export function handleDockPointerDown(
  event: ReactPointerEvent<HTMLDivElement>,
  setDockPosition: Dispatch<SetStateAction<DockPosition>>,
  setIsDragging: (isDragging: boolean) => void,
  onMoveStart: () => void,
  getVisualBounds?: () => DockVisualBounds,
) {
  if (event.button !== 0) return;
  if ((event.target as Element).closest("button")) return;

  event.preventDefault();
  setIsDragging(true);

  let moved = false;
  let previous = { x: event.clientX, y: event.clientY };

  const handlePointerMove = (moveEvent: PointerEvent) => {
    const step = {
      x: moveEvent.clientX - previous.x,
      y: moveEvent.clientY - previous.y,
    };
    previous = { x: moveEvent.clientX, y: moveEvent.clientY };

    if (!moved && Math.hypot(step.x, step.y) > 2) {
      moved = true;
      onMoveStart();
    }

    setDockPosition((position) =>
      clampDockPosition(
        {
          x: position.x + step.x,
          y: position.y + step.y,
        },
        getVisualBounds?.(),
      ),
    );
  };

  const handlePointerEnd = () => {
    setIsDragging(false);
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerEnd);
    window.removeEventListener("pointercancel", handlePointerEnd);
    window.removeEventListener("blur", handlePointerEnd);
    setDockPosition((position) => {
      const clamped = clampDockPosition(position, getVisualBounds?.());
      writeDockPosition(clamped);
      return clamped;
    });
  };

  window.addEventListener("pointermove", handlePointerMove);
  window.addEventListener("pointerup", handlePointerEnd);
  window.addEventListener("pointercancel", handlePointerEnd);
  window.addEventListener("blur", handlePointerEnd);
}

export function readContextEvent(event: Event): VoiceCaoContext | null {
  const eventName = event.type;
  const detail = ((event as CustomEvent).detail ?? {}) as Record<
    string,
    unknown
  >;
  if (eventName === "fintheon:consul-control-market-context") {
    const symbol = readText(detail.symbol);
    return {
      kind: "price",
      label: readText(detail.note) ?? "Price action",
      detail: [symbol, readText(detail.timeframe)].filter(Boolean).join(" "),
    };
  }
  if (eventName === "fintheon:consul-control-read-visible") {
    return {
      kind: "source",
      label: "Visible screen",
      detail: readText(detail.selector) ?? readUrlHost(detail.url) ?? "Read",
    };
  }
  if (eventName === "fintheon:consul-control-chart-context") {
    return {
      kind: "chart",
      label: readText(detail.label) ?? "Chart context",
      detail: readText(detail.symbol) ?? "Chart",
    };
  }
  if (eventName === "fintheon:consul-control-agent-task") {
    return {
      kind: "task",
      label: "Agent task",
      detail: readText(detail.lane) ?? "Queued",
    };
  }
  return null;
}

export function responseForContext(
  context: VoiceCaoContext | null,
  usePreviewScenario: boolean,
  lastUserText: string,
  lastAssistantText: string,
): ResponseScenario {
  const assistantReply = lastAssistantText.trim();
  const prompt = lastUserText.trim();
  if (assistantReply) {
    const conciseReply =
      assistantReply.length > 180
        ? "Open Chat for the full answer."
        : assistantReply;
    return {
      kind: "open-chat",
      phrase: prompt ? "Answering voice request" : "Harper replied",
      stream: assistantReply,
      tools: context ? [toolForContext(context)] : [],
      openChat: {
        label: "",
        detail: conciseReply,
        href: "#open-chat",
      },
      textRows: buildReplyRows(assistantReply),
    };
  }
  if (prompt) {
    return buildPendingVoiceResponse({ prompt, context });
  }
  if (usePreviewScenario) return responseScenarios[0];
  if (!context) return responseScenarios[3];
  if (context.kind === "chart" || context.kind === "price") {
    return responseScenarios[0];
  }
  if (context.kind === "source") return responseScenarios[3];
  return responseScenarios[2];
}

export function readDockPosition(): DockPosition {
  const fallback = getDefaultDockPosition();
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(DOCK_POSITION_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<DockPosition>;
    if (typeof parsed.x === "number" && typeof parsed.y === "number") {
      return clampDockPosition({ x: parsed.x, y: parsed.y });
    }
  } catch {
    /* ignore */
  }
  return fallback;
}

export function responseScenarioIndex(response: ResponseScenario): number {
  return Math.max(
    0,
    responseScenarios.findIndex((scenario) => scenario.kind === response.kind),
  );
}

export function readLocalConsulControlPreview(): boolean {
  if (typeof window === "undefined") return false;
  const isLocalHost = /^(localhost|127\.0\.0\.1|::1)$/i.test(
    window.location.hostname,
  );
  if (!isLocalHost) return false;
  return (
    new URLSearchParams(window.location.search).get("consulControl") === "1"
  );
}

function writeDockPosition(position: DockPosition): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DOCK_POSITION_KEY, JSON.stringify(position));
  } catch {
    /* ignore */
  }
}

function getDefaultDockPosition(): DockPosition {
  if (typeof window === "undefined") return { x: 920, y: 72 };
  return clampDockPosition({
    x: window.innerWidth - 360,
    y: 72,
  });
}

export function measureDockVisualBounds(
  stage: HTMLElement | null,
): DockVisualBounds {
  if (!stage) return DEFAULT_DOCK_VISUAL_BOUNDS;
  const stageRect = stage.getBoundingClientRect();
  const measured = [
    stage,
    ...stage.querySelectorAll<HTMLElement>(
      ".voice-cao-top-drawer, .voice-cao-dock, .voice-cao-bottom-drawer",
    ),
  ].map((element) => element.getBoundingClientRect());
  return {
    left: Math.min(...measured.map((rect) => rect.left)) - stageRect.left,
    top: Math.min(...measured.map((rect) => rect.top)) - stageRect.top,
    right: Math.max(...measured.map((rect) => rect.right)) - stageRect.left,
    bottom: Math.max(...measured.map((rect) => rect.bottom)) - stageRect.top,
  };
}

export function clampDockPosition(
  position: DockPosition,
  bounds: DockVisualBounds = DEFAULT_DOCK_VISUAL_BOUNDS,
): DockPosition {
  if (typeof window === "undefined") return position;
  const minX = 12 - bounds.left;
  const maxX = window.innerWidth - 12 - bounds.right;
  const minY = 12 - bounds.top;
  const maxY = window.innerHeight - 12 - bounds.bottom;
  return {
    x: Math.max(minX, Math.min(Math.max(minX, maxX), position.x)),
    y: Math.max(minY, Math.min(Math.max(minY, maxY), position.y)),
  };
}

function toolForContext(context: VoiceCaoContext) {
  if (context.kind === "chart" || context.kind === "price") {
    return {
      icon: "consul" as const,
      name: "QuickScout",
      action: context.label,
    };
  }
  if (context.kind === "source") {
    return {
      icon: "openui" as const,
      name: "OpenUI",
      action: context.label,
    };
  }
  return {
    icon: "riskflow" as const,
    name: "RiskFlow",
    action: context.label,
  };
}

function buildReplyRows(reply: string): string[] {
  return reply
    .split(/\n+/)
    .flatMap((line) => line.split(/(?<=[.!?])\s+/))
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 8);
}

function readText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function readUrlHost(value: unknown): string | null {
  const raw = readText(value);
  if (!raw) return null;
  try {
    return new URL(raw).hostname;
  } catch {
    return null;
  }
}
